import type React from 'react';
import { useState } from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { CircularProgress, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { drawGeneratedAtomFamily } from 'atoms/events';
import StandardButton from 'components/buttons/StandardButton';
import FormGroup from 'components/dialogs/penaltyDialog/components/FormGroup';
import FormLabel from 'components/dialogs/penaltyDialog/components/FormLabel';
import TMXDialog from 'components/dialogs/TMXDialog';
import { useStyles } from 'components/panels/eventsSplitDraws/selectedEvent/styles';
import {
  getFormatOptions,
  getFormatTypeOptions,
  getGenderOptions,
  getListTypeOptions,
} from 'components/panels/eventsSplitDraws/selectedEvent/utils';
import { BodyLarge, BodyLargeBold, H3 } from 'components/typography/Typography';
import { useGenderDisplayName } from 'hooks/event/useGenderDisplayName';
import { useRankingsAndRatingsContext } from 'hooks/rankingsAndRatings/useRankingsAndRatingsContext';

import { COMBINED, INDIVIDUAL, MIXED, SEEDING, STANDING } from './constants';
import FormatSelect from './selectors/FormatSelect';
import GenderSelect from './selectors/GenderSelect';
import ListTypeSelect from './selectors/ListTypeSelect';
import PublishDateSelect from './selectors/PublishDatesSelect';
import { type FormData, type Option } from './types';

interface InfoDialogProps {
  open: boolean;
  closeDialog: () => void;
  publishedAt: any;
  updatedAt: any;
  eventCategory: string;
  useDualLists: boolean;
}

const RankingsDialog: React.FC<React.PropsWithChildren<InfoDialogProps>> = ({
  open,
  closeDialog,
  publishedAt,
  updatedAt,
  eventCategory,
  useDualLists,
}) => {
  const { classes } = useStyles();
  const { eventId } = useParams<{ eventId: string }>();
  const drawGenerated = useAtomValue(drawGeneratedAtomFamily(eventId));
  const { getRankingsListData, getRankingsListLoading, rankingsConfig, getRankingList, getRankingsList } =
    useRankingsAndRatingsContext();

  const { t } = useTranslation();
  const paperProps = { style: { padding: 30, minWidth: 580 } };
  const whatUpdated = updatedAt.rankingAccessDate;
  const whatUpdatedMens = updatedAt.mensRankingAccessDate;
  const whatUpdatedWomens = updatedAt.womensRankingAccessDate;
  const [listTypeOptions, setListTypeOptions] = useState<Option[]>(getListTypeOptions(rankingsConfig?.isJunior));
  const [formatOptions, setFormatOptions] = useState<Option[]>(
    getFormatOptions(
      rankingsConfig?.isJunior,
      rankingsConfig?.listType,
      rankingsConfig?.formatType,
      rankingsConfig?.isMixed,
      rankingsConfig?.isWheelchair,
    ),
  );
  const [formatTypeOptions, setFormatTypeOptions] = useState<Option[]>(
    getFormatTypeOptions(
      rankingsConfig?.isJunior,
      rankingsConfig?.isNTRP,
      rankingsConfig?.isFamily,
      rankingsConfig?.isWheelchair,
    ),
  );
  const [genderOptions] = useState<Option[]>(getGenderOptions(rankingsConfig?.isJunior));
  const initialValues: FormData = {
    listType: listTypeOptions ? listTypeOptions[0]?.value : SEEDING,
    format: formatOptions
      ? formatOptions[0]?.value
      : rankingsConfig?.isMixed
        ? MIXED
        : rankingsConfig?.isDoubles
          ? EventTypeEnum.DOUBLES
          : EventTypeEnum.SINGLES,
    publishDate: getRankingsListData?.ranklists[0]?.id ? getRankingsListData?.ranklists[0]?.id : '0',
    formatType: formatTypeOptions ? formatTypeOptions[0]?.value : INDIVIDUAL,
  };
  const { male: mensPrefix, female: womensPrefix } = useGenderDisplayName(eventCategory);

  const handleListTypeChange = (event, setFieldValue) => {
    const listTypeOptions = getListTypeOptions(event.target.value);
    setListTypeOptions(listTypeOptions);
    const formats = getFormatOptions(
      rankingsConfig?.isJunior,
      event.target.value,
      '',
      rankingsConfig?.isMixed,
      rankingsConfig?.isWheelchair,
    );
    setFormatOptions(formats);
    rankingsConfig?.setListType(event.target.value);
    setFieldValue('listType', event.target.value, false);

    const format = rankingsConfig?.isJunior
      ? event.target.value === STANDING
        ? COMBINED
        : rankingsConfig?.isDoubles
          ? EventTypeEnum.DOUBLES
          : EventTypeEnum.SINGLES
      : EventTypeEnum.DOUBLES;
    rankingsConfig?.setFormat(format);
    setFieldValue('format', format, false);
    getRankingsList(event.target.value, format, rankingsConfig?.formatType, rankingsConfig?.gender);
  };

  const handleFormatChange = (event) => {
    rankingsConfig?.setFormat(event.target.value);
    getRankingsList(rankingsConfig?.listType, event.target.value, rankingsConfig?.formatType, rankingsConfig?.gender);
  };

  const handleGenderChange = (event) => {
    rankingsConfig?.setGender(event.target.value);
    getRankingsList(rankingsConfig?.listType, rankingsConfig?.format, rankingsConfig?.formatType, event.target.value);
  };

  const handleFormatTypeChange = (event, setFieldValue) => {
    const format = getFormatOptions(
      rankingsConfig?.isJunior,
      '',
      event.target.value,
      rankingsConfig?.isMixed,
      rankingsConfig?.isWheelchair,
    );
    setFormatOptions(format);
    setFieldValue('format', format[0].value, false);
    rankingsConfig.setFormat(format[0].value);
    rankingsConfig?.setFormatType(event.target.value);
    getRankingsList(rankingsConfig?.listType, format[0].value, event.target.value, rankingsConfig?.gender);
  };

  const handlePublishDateChange = (event, setFieldValue) => {
    rankingsConfig?.setSelectedPublishDate(event.target.value);
    setFieldValue('publishDate', event.target.value, false);
  };
  const handleOnEnter = () => {
    const updatedFormatOptions = getFormatOptions(
      rankingsConfig?.isJunior,
      rankingsConfig?.listType,
      rankingsConfig?.formatType,
      rankingsConfig?.isMixed,
      rankingsConfig?.isWheelchair,
    );

    const updatedFormatType = getFormatTypeOptions(
      rankingsConfig?.isJunior,
      rankingsConfig?.isNTRP,
      rankingsConfig?.isFamily,
      rankingsConfig?.isWheelchair,
    );

    setFormatTypeOptions(updatedFormatType);
    setFormatOptions(updatedFormatOptions);
    getRankingsList();
  };
  const handleSubmit = (values: FormData) => {
    getRankingList(values?.publishDate, true);
    rankingsConfig?.setLoadingList(true);
    closeDialog();
  };

  return (
    <TMXDialog
      open={rankingsConfig?.loadingList ? true : open}
      PaperProps={paperProps}
      TransitionProps={{ onEnter: handleOnEnter }}
      onClose={closeDialog}
    >
      <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={handleSubmit}>
        {({ isValid, isSubmitting, setFieldValue, values }) => {
          return rankingsConfig?.loadingList ? (
            <CircularProgress className={classes.loading} />
          ) : (
            <Form>
              <DialogTitle id="rankings-and-ratings-dialog-title">
                <Grid container justifyContent="space-between">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <H3>{t('updateRankingList')}</H3>
                  </Grid>
                  {useDualLists ? (
                    <>
                      {!publishedAt?.mensRankingPubDate ? (
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <BodyLarge>{`${mensPrefix}: ${t('NoUpdateApplyList')}`}</BodyLarge>
                        </Grid>
                      ) : (
                        <Grid item className={classes.subtitle} lg={12} md={12} sm={12} xs={12}>
                          <BodyLarge>{`${mensPrefix} ${t('UpdateApplyList')}`}</BodyLarge>
                          <BodyLargeBold>&nbsp;{whatUpdatedMens}</BodyLargeBold>
                        </Grid>
                      )}
                      {!publishedAt?.womensRankingPubDate ? (
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <BodyLarge>{`${womensPrefix}: ${t('NoUpdateApplyList')}`}</BodyLarge>
                        </Grid>
                      ) : (
                        <Grid item className={classes.subtitle} lg={12} md={12} sm={12} xs={12}>
                          <BodyLarge>{`${womensPrefix} ${t('UpdateApplyList')}`}</BodyLarge>
                          <BodyLargeBold>&nbsp;{whatUpdatedWomens}</BodyLargeBold>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <>
                      {!publishedAt?.rankingPubDate ? (
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <BodyLarge>{t('rankingPubDateNoUpdateList')}</BodyLarge>
                        </Grid>
                      ) : (
                        <Grid item className={classes.subtitle} lg={12} md={12} sm={12} xs={12}>
                          <BodyLarge>{t('rankingPubDateUpdateList')}</BodyLarge>
                          <BodyLargeBold>&nbsp;{whatUpdated}</BodyLargeBold>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </DialogTitle>
              {getRankingsListLoading ? (
                <CircularProgress className={classes.loading} />
              ) : (
                <DialogContent>
                  <>
                    {rankingsConfig?.isMixed && !rankingsConfig?.isFamily && !rankingsConfig?.isWheelchair && (
                      <FormGroup>
                        <FormLabel htmlFor="gender">{t('gdr')}</FormLabel>
                        <Field
                          component={GenderSelect}
                          id="gender"
                          name="gender"
                          options={genderOptions}
                          value={rankingsConfig?.gender}
                          onChange={(e) => handleGenderChange(e)}
                        />
                      </FormGroup>
                    )}
                    {rankingsConfig?.isJunior ? (
                      <FormGroup>
                        <FormLabel htmlFor="listType">{t('listType')}</FormLabel>
                        <Field
                          component={ListTypeSelect}
                          id="listType"
                          name="listType"
                          options={listTypeOptions}
                          value={rankingsConfig?.listType}
                          onChange={(e) => handleListTypeChange(e, setFieldValue)}
                        />
                      </FormGroup>
                    ) : (
                      <FormGroup>
                        <FormLabel htmlFor="formatType">{t('listType')}</FormLabel>
                        <Field
                          component={FormatSelect}
                          id="formatType"
                          name="formatType"
                          options={formatTypeOptions}
                          value={rankingsConfig?.formatType}
                          onChange={(e) => handleFormatTypeChange(e, setFieldValue)}
                        />
                      </FormGroup>
                    )}
                    <FormGroup>
                      <FormLabel htmlFor="format">{t('fmt')}</FormLabel>
                      <Field
                        component={FormatSelect}
                        id="format"
                        name="format"
                        options={formatOptions}
                        value={rankingsConfig?.format}
                        onChange={(e) => handleFormatChange(e)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel htmlFor="publishDate">{t('publishDateText')}</FormLabel>
                      <Field
                        component={PublishDateSelect}
                        id="publishDate"
                        name="publishDate"
                        options={rankingsConfig?.availableDates}
                        value={rankingsConfig?.selectedPublishDate}
                        onChange={(e) => handlePublishDateChange(e, setFieldValue)}
                      />
                    </FormGroup>
                  </>
                </DialogContent>
              )}
              <DialogActions>
                <StandardButton onClick={closeDialog}>{t('cancel')}</StandardButton>
                <StandardButton
                  disabled={!isValid || isSubmitting || values.publishDate === '0' || drawGenerated}
                  loading={rankingsConfig?.loadingList}
                  type="submit"
                  variant="primary"
                >
                  {t('applyUpdate')}
                </StandardButton>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </TMXDialog>
  );
};

export default RankingsDialog;
